<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  onActivated,
  onDeactivated,
  inject,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { RouterLink, useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useCrmAgents } from 'dashboard/composables/useCrmAgents';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import { useKanbanRefresh } from 'dashboard/composables/useKanbanRefresh';
import { useBeforeUnloadGuard } from 'dashboard/composables/useBeforeUnloadGuard';
import { rebaseDealDraft } from 'dashboard/helper/kanbanDraft';
import kanbanClient from 'dashboard/api/kanban/kanbanClient';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import DealNextAction from 'dashboard/routes/dashboard/kanban/DealNextAction.vue';
import DealContextTabs from 'dashboard/routes/dashboard/kanban/DealContextTabs.vue';
import DealLabels from 'dashboard/routes/dashboard/kanban/DealLabels.vue';
import DealExportButton from 'dashboard/routes/dashboard/kanban/DealExportButton.vue';
import DealConflict from 'dashboard/routes/dashboard/kanban/DealConflict.vue';
import DealProductsEditor from 'dashboard/routes/dashboard/kanban/DealProductsEditor.vue';
import CrmTasks from 'dashboard/routes/dashboard/kanban/CrmTasks.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const { accountId, accountScopedRoute } = useAccount();
const {
  agentsList,
  hasError: agentsError,
  isPending: agentsLoading,
  load: reloadAgents,
} = useCrmAgents();

const deals = ref([]);
const taskEditor = ref(null);
const dataError = ref(false);
const orderedDeals = computed(() =>
  [...deals.value].sort(
    (a, b) =>
      Number(b.linked_to_conversation === true) -
      Number(a.linked_to_conversation === true)
  )
);
const noLinkedDeal = computed(
  () =>
    deals.value.length > 0 &&
    deals.value.every(deal => deal.linked_to_conversation === false)
);
const contextLabel = deal => {
  if (deal.linked_to_conversation === true)
    return t('KANBAN.SIDEBAR.LINKED_CONVERSATION');
  if (deal.linked_to_conversation === false)
    return t('KANBAN.SIDEBAR.OTHER_CONTACT_DEAL');
  return t('KANBAN.SIDEBAR.UNKNOWN_CONVERSATION_LINK');
};
const funnels = ref([]);
const stages = ref([]);
const isLoading = ref(true);
const isCreating = ref(false);
const isSaving = ref(false);
const isMoving = ref(false);
let dataRequest = 0;
const isActive = ref(true);
const sidebarAccountId = accountId.value;

// New deal form
const showNewDeal = ref(false);
const newDealFunnelId = ref(null);
const newDealStageId = ref(null);
const newDealTitle = ref('');
const newDealValue = ref('');
const newDealProduct = ref('');
const newDealAssigneeId = ref(null);
const newDealNotes = ref('');
const newDealNextAction = ref('');
const newDealNextActionDate = ref('');

const editingDealId = ref(null);
const editingOriginal = ref(null);
const editBaseSnapshot = ref({});
const dealConflict = ref(null);
const dealUnavailable = ref(false);
const editSourceFunnelId = ref(null);
const editDealFunnelId = ref(null);
const editDealStageId = ref(null);
const editDealAssigneeId = ref(null);
const editDealTitle = ref('');
const editDealLabels = ref([]);
const editDealValue = ref('');
const editDealProduct = ref('');
const editDealProducts = ref([]);
const editDealSource = ref('');
const editDealCloseDate = ref('');
const editDealNotes = ref('');
const editDealNextAction = ref('');
const editDealNextActionDate = ref('');
const editStages = ref([]);
const editSnapshot = () => ({
  funnel: editDealFunnelId.value,
  stage: editDealStageId.value,
  assignee: editDealAssigneeId.value,
  title: editDealTitle.value.trim(),
  labels: editDealLabels.value,
  value: editDealValue.value,
  product: editDealProduct.value,
  products: editDealProducts.value,
  source: editDealSource.value,
  closeDate: editDealCloseDate.value,
  notes: editDealNotes.value,
  nextAction: editDealNextAction.value,
  nextDate: editDealNextActionDate.value,
});

const currentChat = useMapGetter('getSelectedChat');
const contactId = computed(() => currentChat.value?.meta?.sender?.id);
const isCurrentConversation = computed(
  () =>
    isActive.value &&
    String(accountId.value) === String(sidebarAccountId) &&
    String(currentChat.value?.account_id) === String(accountId.value) &&
    String(currentChat.value?.id) === String(props.conversationId)
);
const stagesForFunnel = funnelId =>
  funnels.value.find(funnel => funnel.id === funnelId)?.kanban_stages || [];

const updateReturn = updatedDeal => {
  const deal = deals.value.find(item => item.id === updatedDeal.id);
  if (deal) deal.next_return = updatedDeal.next_return;
};

const loadStages = async funnelId => {
  if (!funnelId) return;
  const funnel = funnels.value.find(f => f.id === funnelId);
  let nextStages = [];
  if (funnel?.kanban_stages?.length) {
    nextStages = funnel.kanban_stages;
  } else {
    try {
      const { data } = await kanbanClient.getStages(funnelId);
      nextStages = data;
    } catch {
      nextStages = [];
    }
  }
  if (newDealFunnelId.value !== funnelId) return;
  stages.value = nextStages;
  if (!stages.value.some(stage => stage.id === newDealStageId.value)) {
    newDealStageId.value = stages.value[0]?.id || null;
  }
};

const updateEditStages = () => {
  editStages.value = stagesForFunnel(editDealFunnelId.value);
  if (!editStages.value.some(stage => stage.id === editDealStageId.value)) {
    editDealStageId.value = editStages.value[0]?.id || null;
  }
};

const fetchData = async (background = false) => {
  dataRequest += 1;
  const request = dataRequest;
  if (!isCurrentConversation.value) return;
  dataError.value = false;
  if (background !== true) {
    isLoading.value = true;
    deals.value = [];
    funnels.value = [];
    stages.value = [];
  }
  try {
    const [funnelsRes, dealsRes] = await Promise.all([
      kanbanClient.getFunnels(),
      contactId.value
        ? kanbanClient.lookupDealsByContact(contactId.value, {
            conversation_display_id: props.conversationId,
          })
        : Promise.resolve({ data: [] }),
    ]);
    if (request !== dataRequest) return;
    funnels.value = funnelsRes.data;
    deals.value = dealsRes.data;
    const current = deals.value.find(deal => deal.id === editingDealId.value);
    if (background === true && editingOriginal.value) {
      dealUnavailable.value = !current;
      if (!current) {
        deals.value = [...deals.value, editingOriginal.value];
        dealConflict.value = null;
      }
    }
    if (
      background === true &&
      current &&
      current.lock_version !== editingOriginal.value?.lock_version
    ) {
      dealConflict.value = current;
    }

    if (!funnels.value.some(funnel => funnel.id === newDealFunnelId.value)) {
      newDealFunnelId.value = funnels.value[0]?.id || null;
    }
    await loadStages(newDealFunnelId.value);
  } catch {
    if (request === dataRequest) {
      dataError.value = true;
    }
  } finally {
    if (request === dataRequest) isLoading.value = false;
  }
};

const resetNewDealFields = () => {
  newDealTitle.value = '';
  newDealValue.value = '';
  newDealProduct.value = '';
  newDealAssigneeId.value = null;
  newDealNotes.value = '';
  newDealNextAction.value = '';
  newDealNextActionDate.value = '';
};

const availableProducts = ref([]);
const productsError = ref(false);
const productsLoaded = ref(false);
const { run: runProducts, isPending: productsPending } = useAbortableRequest();
const fetchProducts = async () => {
  if (productsLoaded.value || productsPending.value) return;
  productsError.value = false;
  try {
    await runProducts(async signal => {
      const { data } = await kanbanClient.getProducts();
      if (!signal.aborted) {
        availableProducts.value = data;
        productsLoaded.value = true;
      }
    });
  } catch {
    productsError.value = true;
  }
};

const dealProductsForEdit = deal =>
  (deal.deal_products || []).map(dealProduct => {
    const productId = dealProduct.product?.id || dealProduct.product_id;
    const cachedProduct = availableProducts.value.find(
      product => product.id === productId
    );
    return {
      product_id: productId,
      value: dealProduct.value == null ? null : Number(dealProduct.value),
      name: dealProduct.product?.name || cachedProduct?.name || '',
    };
  });

const recalcEditDealValue = products => {
  if (products.some(product => product.value === null || product.value === ''))
    return;
  editDealValue.value = products.reduce(
    (sum, product) => sum + (parseFloat(product.value) || 0),
    0
  );
};

const addEditDealProduct = productId => {
  const id = Number(productId);
  if (!id || editDealProducts.value.some(product => product.product_id === id))
    return;
  const product = availableProducts.value.find(item => item.id === id);
  if (!product) return;
  editDealProducts.value = [
    ...editDealProducts.value,
    {
      product_id: id,
      value: product.min_price == null ? null : Number(product.min_price),
      name: product.name,
    },
  ];
};

const removeEditDealProduct = index => {
  editDealProducts.value = editDealProducts.value.filter(
    (_product, productIndex) => productIndex !== index
  );
};

const updateEditDealProductValue = ({ index, value }) => {
  editDealProducts.value = editDealProducts.value.map(
    (product, productIndex) =>
      productIndex === index ? { ...product, value } : product
  );
  recalcEditDealValue(editDealProducts.value);
};

const createDeal = async () => {
  if (
    isCreating.value ||
    !contactId.value ||
    !newDealFunnelId.value ||
    !newDealStageId.value
  )
    return;
  const conversationId = props.conversationId;
  dataRequest += 1;
  const request = dataRequest;
  isCreating.value = true;
  try {
    await kanbanClient.createDeal(newDealFunnelId.value, {
      contact_id: contactId.value,
      kanban_stage_id: newDealStageId.value,
      title: newDealTitle.value.trim() || null,
      deal_value: newDealValue.value,
      product_name: newDealProduct.value || '',
      assignee_id: newDealAssigneeId.value || null,
      notes: newDealNotes.value,
      custom_fields: {
        next_action: newDealNextAction.value,
        next_action_due_date: newDealNextActionDate.value || null,
      },
      conversation_display_ids: [conversationId],
    });
    if (conversationId !== props.conversationId || request !== dataRequest)
      return;
    useAlert(t('KANBAN.DEAL_CREATED'));
    showNewDeal.value = false;
    resetNewDealFields();
    await fetchData(true);
  } catch {
    if (conversationId === props.conversationId && request === dataRequest)
      useAlert(t('KANBAN.ERRORS.CREATE_DEAL'));
  } finally {
    isCreating.value = false;
  }
};

const startEditDeal = deal => {
  if (taskEditor.value?.selectDeal(deal.id) === false) {
    useAlert(t('KANBAN.UNSAVED_DEAL'));
    return;
  }
  fetchProducts();
  dealUnavailable.value = false;
  editingDealId.value = deal.id;
  editingOriginal.value = deal;
  dealConflict.value = null;
  editSourceFunnelId.value = deal.kanban_funnel_id || deal.kanban_funnel?.id;
  editDealFunnelId.value = editSourceFunnelId.value;
  editDealStageId.value = deal.kanban_stage?.id;
  editDealAssigneeId.value = deal.assignee?.id || null;
  editDealTitle.value = deal.title || '';
  editDealLabels.value = (deal.labels || []).map(label => label.name).sort();
  editDealValue.value = deal.deal_value ?? '';
  editDealProduct.value = deal.product_name || '';
  editDealProducts.value = dealProductsForEdit(deal);
  editDealSource.value = deal.lead_source || '';
  editDealCloseDate.value = deal.expected_close_date || '';
  editDealNotes.value = deal.notes || '';
  editDealNextAction.value = deal.custom_fields?.next_action || '';
  editDealNextActionDate.value = deal.custom_fields?.next_action_due_date || '';
  updateEditStages();
  editBaseSnapshot.value = editSnapshot();
};

const cancelEditDeal = () => {
  if (dealUnavailable.value) {
    deals.value = deals.value.filter(deal => deal.id !== editingDealId.value);
  }
  dealUnavailable.value = false;
  editingDealId.value = null;
  editingOriginal.value = null;
  dealConflict.value = null;
};

const reviewDealChanges = () => {
  const original = editBaseSnapshot.value;
  const draft = editSnapshot();
  startEditDeal(dealConflict.value);
  const merged = rebaseDealDraft(original, draft, editBaseSnapshot.value);
  editDealFunnelId.value = merged.funnel;
  editDealStageId.value = merged.stage;
  editDealAssigneeId.value = merged.assignee;
  editDealTitle.value = merged.title;
  editDealLabels.value = merged.labels;
  editDealValue.value = merged.value;
  editDealProduct.value = merged.product;
  editDealProducts.value = merged.products;
  editDealSource.value = merged.source;
  editDealCloseDate.value = merged.closeDate;
  editDealNotes.value = merged.notes;
  editDealNextAction.value = merged.nextAction;
  editDealNextActionDate.value = merged.nextDate;
  updateEditStages();
};

const saveDeal = async deal => {
  if (
    isSaving.value ||
    dealConflict.value ||
    dealUnavailable.value ||
    !editDealFunnelId.value ||
    !editDealStageId.value
  )
    return;
  const conversationId = props.conversationId;
  dataRequest += 1;
  const request = dataRequest;
  const dealProducts = editDealProducts.value.map(product => ({
    product_id: product.product_id,
    value: product.value === '' ? null : product.value,
  }));
  const originalProducts = (editingOriginal.value.deal_products || []).map(
    product => ({
      product_id: product.product?.id || product.product_id,
      value: product.value == null ? null : Number(product.value),
    })
  );
  const productsChanged =
    JSON.stringify(dealProducts) !== JSON.stringify(originalProducts);
  isSaving.value = true;
  try {
    await kanbanClient.updateDeal(editSourceFunnelId.value, deal.id, {
      lock_version: editingOriginal.value.lock_version,
      kanban_funnel_id: editDealFunnelId.value,
      kanban_stage_id: editDealStageId.value,
      assignee_id: editDealAssigneeId.value || null,
      title: editDealTitle.value.trim() || null,
      ...(Array.isArray(editingOriginal.value.labels)
        ? { label_list: editDealLabels.value }
        : {}),
      deal_value: editDealValue.value,
      product_name:
        productsChanged && editDealProducts.value.length
          ? editDealProducts.value.map(product => product.name).join(', ')
          : editDealProduct.value,
      ...(productsChanged ? { deal_products: dealProducts } : {}),
      ...(editDealSource.value !== (editingOriginal.value.lead_source || '')
        ? { lead_source: editDealSource.value }
        : {}),
      ...(editDealCloseDate.value !==
      (editingOriginal.value.expected_close_date || '')
        ? { expected_close_date: editDealCloseDate.value || null }
        : {}),
      notes: editDealNotes.value,
      custom_fields: {
        next_action: editDealNextAction.value,
        next_action_due_date: editDealNextActionDate.value || null,
      },
    });
    if (conversationId !== props.conversationId || request !== dataRequest)
      return;
    useAlert(t('KANBAN.DEAL_SAVED'));
    cancelEditDeal();
    await fetchData(true);
  } catch (error) {
    if (
      conversationId === props.conversationId &&
      request === dataRequest &&
      error.response?.status === 409
    ) {
      dealConflict.value = error.response.data.deal;
    }
    if (conversationId === props.conversationId && request === dataRequest)
      useAlert(t('KANBAN.ERRORS.SAVE_DEAL'));
  } finally {
    isSaving.value = false;
  }
};

const moveDeal = async (deal, stageId) => {
  if (isMoving.value || deal.kanban_stage?.id === stageId) return;
  const conversationId = props.conversationId;
  dataRequest += 1;
  const request = dataRequest;
  isMoving.value = true;
  try {
    await kanbanClient.moveDeal(
      deal.kanban_funnel?.id,
      deal.id,
      stageId,
      deal.lock_version
    );
    if (conversationId !== props.conversationId || request !== dataRequest)
      return;
    useAlert(t('KANBAN.SIDEBAR.MOVED'));
    await fetchData(true);
  } catch {
    if (conversationId === props.conversationId && request === dataRequest)
      useAlert(t('KANBAN.ERRORS.MOVE_DEAL'));
  } finally {
    isMoving.value = false;
  }
};

useKanbanRefresh(
  () => fetchData(true),
  () =>
    isCurrentConversation.value &&
    !isSaving.value &&
    !isCreating.value &&
    !isMoving.value &&
    !isLoading.value
);

const kanbanReturnQuery = () => {
  const query = {};
  if (route.query.kanban_account === String(route.params.accountId)) {
    [
      'funnel',
      'deal',
      'stage',
      'filter',
      'search',
      'view',
      'assignee',
      'next_action',
      'page',
    ].forEach(key => {
      query[key] = route.query[`kanban_${key}`];
    });
  }
  return query;
};

const dealRoute = deal => {
  const query = kanbanReturnQuery();
  const funnel = String(deal.kanban_funnel.id);
  const id = String(deal.id);
  return accountScopedRoute(
    'kanban_index',
    {},
    {
      ...(query.funnel === funnel && query.deal === id ? query : {}),
      funnel,
      deal: id,
    }
  );
};

const openKanban = () => {
  router.push(accountScopedRoute('kanban_index', {}, kanbanReturnQuery()));
};

const formatValue = val => {
  const num = parseFloat(val);
  if (Number.isNaN(num)) return null;
  return `R$ ${num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const hasUnsavedDealChanges = computed(() => {
  const edited =
    editingDealId.value &&
    JSON.stringify(editSnapshot()) !== JSON.stringify(editBaseSnapshot.value);
  return Boolean(
    showNewDeal.value ||
      taskEditor.value?.hasUnsavedChanges ||
      edited ||
      isSaving.value ||
      isCreating.value ||
      isMoving.value
  );
});
useBeforeUnloadGuard(hasUnsavedDealChanges);

const guardNavigation = () => {
  if (!hasUnsavedDealChanges.value) return true;
  useAlert(t('KANBAN.UNSAVED_DEAL'));
  return false;
};
// The parent remains active when KeepAlive hides this sidebar.
const navigationGuard = inject('conversationNavigationGuard', null);
if (navigationGuard) {
  navigationGuard.value = guardNavigation;
  onBeforeUnmount(() => {
    if (navigationGuard.value === guardNavigation) {
      navigationGuard.value = () => true;
    }
  });
}

watch(
  [
    accountId,
    () => props.conversationId,
    () => currentChat.value?.id,
    () => currentChat.value?.account_id,
    contactId,
  ],
  () => {
    dataRequest += 1;
    if (!isCurrentConversation.value) return;
    showNewDeal.value = false;
    cancelEditDeal();
    resetNewDealFields();
    newDealFunnelId.value = null;
    newDealStageId.value = null;
    fetchData();
  }
);
watch(newDealFunnelId, id => loadStages(id));
onMounted(fetchData);
const pauseRequests = () => {
  isActive.value = false;
  dataRequest += 1;
};
onDeactivated(pauseRequests);
onBeforeUnmount(pauseRequests);
onActivated(() => {
  if (isActive.value) return;
  isActive.value = true;
  fetchData(true);
});
</script>

<template>
  <fieldset
    :disabled="isSaving || isCreating || isMoving"
    :aria-label="t('KANBAN.SIDEBAR.TITLE')"
    class="min-w-0 flex flex-col gap-2"
  >
    <button
      v-if="agentsError"
      type="button"
      class="text-xs text-n-ruby-11"
      data-test-id="retry-agents"
      @click="reloadAgents"
    >
      {{ t('KANBAN.ERRORS.FETCH_ASSIGNEES') }}
    </button>
    <button
      v-if="dataError"
      type="button"
      data-test-id="retry-deal-context"
      class="text-start text-xs text-n-ruby-11"
      @click="fetchData(true)"
    >
      {{ t('KANBAN.SIDEBAR.CONTEXT_ERROR') }}
    </button>
    <div v-if="isLoading" class="flex items-center justify-center py-4">
      <Spinner :size="16" class="text-n-slate-10" />
    </div>

    <template v-else>
      <p
        v-if="noLinkedDeal && !dataError"
        role="status"
        data-test-id="no-linked-deal"
        class="text-xs leading-5 text-n-slate-11"
      >
        {{ t('KANBAN.SIDEBAR.NO_LINKED_DEAL') }}
      </p>
      <!-- Contact deals list -->
      <div v-if="deals.length" class="flex flex-col gap-2">
        <div
          v-for="deal in orderedDeals"
          :key="deal.id"
          class="border border-n-weak rounded-xl p-3 bg-n-solid-1 shadow-sm"
        >
          <!-- Deal header -->
          <div class="flex items-start gap-2 mb-2">
            <span
              v-if="deal.title || deal.product_name"
              class="text-sm font-semibold text-n-slate-12 break-words min-w-0"
            >
              {{ deal.title || deal.product_name }}
            </span>
            <span v-else class="text-xs text-n-slate-10">
              {{ t('KANBAN.SIDEBAR.TITLE') }}
            </span>
            <span class="flex-1" />
          </div>
          <p
            v-if="editingDealId === deal.id && dealUnavailable"
            role="status"
            class="text-xs text-n-amber-11"
          >
            {{ t('KANBAN.CONFLICT.UNAVAILABLE') }}
          </p>
          <DealConflict
            v-if="editingDealId === deal.id && dealConflict"
            :current="dealConflict"
            @review="reviewDealChanges"
          />
          <DealContextTabs :deal="deal">
            <template #principal>
              <div v-if="editingDealId === deal.id" class="flex flex-col gap-2">
                <label class="flex flex-col gap-1 text-xs text-n-slate-11">
                  {{ t('KANBAN.DEAL_TITLE_LABEL') }}
                  <input
                    v-model="editDealTitle"
                    data-test-id="deal-title"
                    type="text"
                    maxlength="255"
                    :disabled="isSaving || dealUnavailable"
                    :placeholder="t('KANBAN.DEAL_TITLE_PLACEHOLDER')"
                    class="w-full px-2.5 py-1.5 rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:ring-2 focus:ring-n-brand"
                  />
                </label>
                <DealLabels
                  v-model="editDealLabels"
                  :known="Array.isArray(editingOriginal.labels)"
                  editable
                  :disabled="isSaving || dealUnavailable || !!dealConflict"
                />
                <select
                  v-model.number="editDealFunnelId"
                  :aria-label="t('KANBAN.SIDEBAR.SELECT_FUNNEL')"
                  data-test-id="deal-funnel"
                  class="w-full px-2.5 py-1.5 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:ring-2 focus:ring-n-brand"
                  @change="updateEditStages"
                >
                  <option
                    v-for="funnel in funnels"
                    :key="funnel.id"
                    :value="funnel.id"
                  >
                    {{ funnel.name }}
                  </option>
                </select>
                <select
                  v-model.number="editDealStageId"
                  :aria-label="t('KANBAN.SIDEBAR.SELECT_STAGE')"
                  data-test-id="deal-stage"
                  class="w-full px-2.5 py-1.5 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:ring-2 focus:ring-n-brand"
                >
                  <option
                    v-for="stage in editStages"
                    :key="stage.id"
                    :value="stage.id"
                  >
                    {{ stage.name }}
                  </option>
                </select>
                <select
                  v-model.number="editDealAssigneeId"
                  :aria-label="t('CONVERSATION_SIDEBAR.ASSIGNEE_LABEL')"
                  data-test-id="deal-assignee"
                  :disabled="agentsLoading || agentsError"
                  class="w-full px-2.5 py-1.5 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:ring-2 focus:ring-n-brand"
                >
                  <option :value="null">
                    {{ t('KANBAN.SIDEBAR.NO_ASSIGNEE') }}
                  </option>
                  <option
                    v-for="agent in agentsList"
                    :key="agent.id"
                    :value="agent.id"
                  >
                    {{ agent.name }}
                  </option>
                </select>
                <DealProductsEditor
                  :products="editDealProducts"
                  :product-name="editDealProduct"
                  :available-products="availableProducts"
                  :is-pending="productsPending"
                  :has-error="productsError"
                  :disabled="isSaving || !!dealConflict || dealUnavailable"
                  @update:product-name="editDealProduct = $event"
                  @add-product="addEditDealProduct"
                  @remove-product="removeEditDealProduct"
                  @update-product-value="updateEditDealProductValue"
                  @retry="fetchProducts"
                />
                <input
                  v-model="editDealValue"
                  :aria-label="t('KANBAN.DEAL_VALUE_LABEL')"
                  type="number"
                  step="0.01"
                  :placeholder="t('KANBAN.SIDEBAR.DEAL_VALUE_PLACEHOLDER')"
                  class="w-full px-2.5 py-1.5 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:ring-2 focus:ring-n-brand"
                />
                <label class="flex flex-col gap-1 text-xs text-n-slate-11">
                  {{ t('KANBAN.LEAD_SOURCE_LABEL') }}
                  <input
                    v-model="editDealSource"
                    data-test-id="deal-source"
                    type="text"
                    :placeholder="t('KANBAN.LEAD_SOURCE_PLACEHOLDER')"
                    class="w-full px-2.5 py-1.5 rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:ring-2 focus:ring-n-brand"
                  />
                </label>
                <label class="flex flex-col gap-1 text-xs text-n-slate-11">
                  {{ t('KANBAN.CLOSE_DATE_LABEL') }}
                  <input
                    v-model="editDealCloseDate"
                    data-test-id="deal-close-date"
                    type="date"
                    class="w-full min-w-0 px-2.5 py-1.5 rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:ring-2 focus:ring-n-brand"
                  />
                </label>
                <textarea
                  v-model="editDealNotes"
                  :aria-label="t('KANBAN.NOTES_LABEL')"
                  data-test-id="deal-notes"
                  :placeholder="t('KANBAN.NOTES_PLACEHOLDER')"
                  class="w-full px-2.5 py-1.5 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:ring-2 focus:ring-n-brand"
                />
                <DealNextAction
                  v-if="deal.next_return?.source === 'tasks'"
                  :next-return="deal.next_return"
                />
                <input
                  v-else
                  v-model="editDealNextAction"
                  :aria-label="t('KANBAN.SIDEBAR.NEXT_ACTION')"
                  data-test-id="deal-next-action"
                  type="text"
                  :placeholder="t('KANBAN.SIDEBAR.NEXT_ACTION_PLACEHOLDER')"
                  class="w-full px-2.5 py-1.5 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:ring-2 focus:ring-n-brand"
                />
                <label
                  v-if="deal.next_return?.source !== 'tasks'"
                  class="flex flex-col gap-1 text-xs text-n-slate-11"
                >
                  {{ t('KANBAN.SIDEBAR.NEXT_ACTION_DATE') }}
                  <input
                    v-model="editDealNextActionDate"
                    data-test-id="deal-next-action-date"
                    type="date"
                    class="w-full min-w-0 px-2.5 py-1.5 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:ring-2 focus:ring-n-brand"
                  />
                </label>
                <div class="flex gap-2">
                  <Button
                    data-test-id="save-deal"
                    variant="solid"
                    color="blue"
                    size="sm"
                    :label="t('KANBAN.SAVE')"
                    :is-loading="isSaving"
                    :disabled="isSaving || !!dealConflict || dealUnavailable"
                    class="flex-1"
                    @click="saveDeal(deal)"
                  />
                  <Button
                    variant="ghost"
                    color="slate"
                    size="sm"
                    :label="t('KANBAN.CANCEL')"
                    @click="cancelEditDeal"
                  />
                </div>
              </div>
              <template v-else>
                <button
                  :data-test-id="`edit-deal-${deal.id}`"
                  type="button"
                  class="self-start gap-1.5 px-2 text-xs text-n-slate-11 hover:text-n-slate-12 min-h-8 min-w-8 flex items-center justify-center rounded-lg hover:bg-n-alpha-2"
                  :aria-label="t('KANBAN.SIDEBAR.EDIT_DEAL')"
                  :title="t('KANBAN.SIDEBAR.EDIT_DEAL')"
                  @click="startEditDeal(deal)"
                >
                  <span class="i-lucide-pencil size-3.5 block" />
                  {{ t('KANBAN.SIDEBAR.EDIT_DEAL') }}
                </button>
                <p
                  :data-test-id="`deal-link-${deal.id}`"
                  class="mb-2 text-xs"
                  :class="
                    deal.linked_to_conversation === true
                      ? 'text-n-teal-11'
                      : 'text-n-slate-11'
                  "
                >
                  {{ contextLabel(deal) }}
                </p>
                <RouterLink
                  v-if="
                    deal.kanban_funnel?.id && !showNewDeal && !editingDealId
                  "
                  :to="dealRoute(deal)"
                  :data-test-id="`deal-context-${deal.id}`"
                  :title="t('KANBAN.TASKS.OPEN_DEAL')"
                  class="mb-2 flex items-center gap-1 text-xs font-medium text-n-slate-11 hover:text-n-brand focus-visible:ring-2 focus-visible:ring-n-brand rounded"
                >
                  <span
                    class="i-lucide-columns-3 size-3.5 shrink-0"
                    aria-hidden="true"
                  />
                  {{ deal.kanban_funnel.name }}
                  <span
                    class="i-lucide-arrow-up-right size-3.5 shrink-0"
                    aria-hidden="true"
                  />
                </RouterLink>
                <p
                  v-if="deal.title && deal.product_name"
                  class="mb-1 text-xs text-n-slate-11 break-words"
                >
                  {{ deal.product_name }}
                </p>
                <p
                  v-if="formatValue(deal.deal_value)"
                  class="text-base font-semibold tabular-nums text-n-slate-12 mb-3"
                >
                  {{ formatValue(deal.deal_value) }}
                </p>

                <Button
                  v-if="
                    !showNewDeal && !editingDealId && deal.kanban_funnel?.id
                  "
                  :data-test-id="`deal-products-${deal.id}`"
                  variant="ghost"
                  color="slate"
                  size="sm"
                  icon="i-lucide-package-search"
                  :label="t('KANBAN.DEAL_PRODUCTS')"
                  class="mb-3 self-start"
                  @click="startEditDeal(deal)"
                />

                <label class="flex flex-col gap-1 text-xs text-n-slate-11">
                  {{ t('KANBAN.MOVE_STAGE') }}
                  <span class="relative flex items-center">
                    <span
                      class="absolute left-3 size-2 rounded-full"
                      :style="{ backgroundColor: deal.kanban_stage?.color }"
                      aria-hidden="true"
                    />
                    <select
                      :value="deal.kanban_stage?.id"
                      :data-test-id="`move-deal-stage-${deal.id}`"
                      :disabled="isMoving"
                      class="w-full min-h-9 rounded-lg border border-n-weak bg-n-alpha-1 py-2 pl-7 pr-8 text-sm text-n-slate-12 disabled:cursor-wait disabled:opacity-60"
                      @change="
                        moveDeal(deal, Number($event.currentTarget.value))
                      "
                    >
                      <option
                        v-for="stage in deal.kanban_funnel?.kanban_stages ||
                        stages"
                        :key="stage.id"
                        :value="stage.id"
                      >
                        {{ stage.name }}
                      </option>
                    </select>
                  </span>
                </label>

                <!-- Assignee -->
                <div
                  class="mt-3 text-xs text-n-slate-11 flex items-center gap-1.5"
                >
                  <span class="i-lucide-user-round size-3.5 shrink-0" />
                  {{ deal.assignee?.name || t('KANBAN.SIDEBAR.NO_ASSIGNEE') }}
                </div>
                <div
                  v-if="deal.notes"
                  class="mt-2 text-xs leading-5 text-n-slate-11 whitespace-pre-wrap"
                >
                  {{ deal.notes }}
                </div>
                <DealNextAction
                  class="mt-3 rounded-lg bg-n-alpha-1 p-2.5"
                  :action="deal.custom_fields?.next_action || ''"
                  :next-return="deal.next_return"
                  :due-date="deal.custom_fields?.next_action_due_date || ''"
                />
                <DealLabels
                  :model-value="(deal.labels || []).map(label => label.name)"
                  :known="Array.isArray(deal.labels)"
                />
                <DealExportButton
                  :funnel-id="deal.kanban_funnel_id"
                  :deal-id="deal.id"
                />
              </template>
            </template>
          </DealContextTabs>
        </div>
      </div>

      <div
        v-else-if="!showNewDeal && !dataError"
        class="text-xs text-n-slate-10 py-1"
      >
        {{ t('KANBAN.SIDEBAR.NOT_IN_KANBAN') }}
      </div>

      <!-- New deal form -->
      <div
        v-if="showNewDeal"
        class="border border-n-weak rounded-lg p-2 bg-n-solid-2 flex flex-col gap-2"
      >
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          {{ t('KANBAN.DEAL_TITLE_LABEL') }}
          <input
            v-model="newDealTitle"
            data-test-id="new-deal-title"
            type="text"
            maxlength="255"
            :disabled="isCreating"
            :placeholder="t('KANBAN.DEAL_TITLE_PLACEHOLDER')"
            class="w-full px-2.5 py-1.5 rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:ring-2 focus:ring-n-brand"
          />
        </label>
        <select
          v-model="newDealFunnelId"
          data-test-id="new-deal-funnel"
          :aria-label="t('KANBAN.SIDEBAR.SELECT_FUNNEL')"
          class="w-full px-2.5 py-1.5 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:ring-2 focus:ring-n-brand"
        >
          <option v-for="funnel in funnels" :key="funnel.id" :value="funnel.id">
            {{ funnel.name }}
          </option>
        </select>

        <select
          v-model="newDealStageId"
          data-test-id="new-deal-stage"
          :aria-label="t('KANBAN.SIDEBAR.SELECT_STAGE')"
          class="w-full px-2.5 py-1.5 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:ring-2 focus:ring-n-brand"
        >
          <option v-for="stage in stages" :key="stage.id" :value="stage.id">
            {{ stage.name }}
          </option>
        </select>

        <input
          v-model="newDealProduct"
          :aria-label="t('KANBAN.PRODUCT_LABEL')"
          type="text"
          :placeholder="t('KANBAN.PRODUCT_PLACEHOLDER')"
          class="w-full px-2.5 py-1.5 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:ring-2 focus:ring-n-brand"
        />

        <input
          v-model="newDealValue"
          :aria-label="t('KANBAN.DEAL_VALUE_LABEL')"
          type="number"
          step="0.01"
          :placeholder="t('KANBAN.SIDEBAR.DEAL_VALUE_PLACEHOLDER')"
          class="w-full px-2.5 py-1.5 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:ring-2 focus:ring-n-brand"
        />

        <select
          v-model.number="newDealAssigneeId"
          :aria-label="t('CONVERSATION_SIDEBAR.ASSIGNEE_LABEL')"
          data-test-id="new-deal-assignee"
          :disabled="agentsLoading || agentsError"
          class="w-full px-2.5 py-1.5 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:ring-2 focus:ring-n-brand"
        >
          <option :value="null">{{ t('KANBAN.SIDEBAR.NO_ASSIGNEE') }}</option>
          <option v-for="agent in agentsList" :key="agent.id" :value="agent.id">
            {{ agent.name }}
          </option>
        </select>

        <textarea
          v-model="newDealNotes"
          :aria-label="t('KANBAN.NOTES_LABEL')"
          data-test-id="new-deal-notes"
          :placeholder="t('KANBAN.NOTES_PLACEHOLDER')"
          class="w-full px-2.5 py-1.5 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:ring-2 focus:ring-n-brand"
        />

        <input
          v-model="newDealNextAction"
          :aria-label="t('KANBAN.SIDEBAR.NEXT_ACTION')"
          data-test-id="new-deal-next-action"
          type="text"
          :placeholder="t('KANBAN.SIDEBAR.NEXT_ACTION_PLACEHOLDER')"
          class="w-full px-2.5 py-1.5 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:ring-2 focus:ring-n-brand"
        />

        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          {{ t('KANBAN.SIDEBAR.NEXT_ACTION_DATE') }}
          <input
            v-model="newDealNextActionDate"
            data-test-id="new-deal-next-action-date"
            type="date"
            class="w-full min-w-0 px-2.5 py-1.5 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:ring-2 focus:ring-n-brand"
          />
        </label>

        <div class="flex gap-2">
          <Button
            data-test-id="create-deal"
            variant="solid"
            color="blue"
            size="sm"
            :label="t('KANBAN.CREATE')"
            :is-loading="isCreating"
            class="flex-1"
            @click="createDeal"
          />
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            :label="t('KANBAN.CANCEL')"
            @click="showNewDeal = false"
          />
        </div>
      </div>

      <CrmTasks
        v-if="deals.length"
        :key="conversationId"
        ref="taskEditor"
        :deals="
          editingDealId
            ? orderedDeals.filter(deal => deal.id === editingDealId)
            : orderedDeals
        "
        class="border-t border-n-weak pt-3"
        @changed="updateReturn"
      />

      <!-- Actions -->
      <div class="flex gap-2">
        <Button
          v-if="!showNewDeal && funnels.length"
          data-test-id="new-deal"
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-plus"
          :label="t('KANBAN.ADD_DEAL')"
          class="flex-1"
          @click="showNewDeal = true"
        />
        <Button
          v-if="deals.length"
          variant="ghost"
          color="slate"
          size="sm"
          icon="i-lucide-target"
          :aria-label="t('KANBAN.TITLE')"
          :title="t('KANBAN.TITLE')"
          @click="openKanban"
        />
      </div>
    </template>
  </fieldset>
</template>
